import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '@/components/feature/BottomNav';
import { useTrip } from '@/context/TripContext';
import { places } from '@/mocks/placesData';

export default function MyTrips() {
  const navigate = useNavigate();
  const { selectedSpots, spotStatus, currentRoute, getNextSpot, getCompletedCount } = useTrip();

  const hasCurrentRoute = !!currentRoute;

  // 只收集真实点位（isRealPlace === true），不展示建议/转场节点
  const allRealSpots: { id: string; name: string; day: number; status: string; placeId?: string }[] = [];
  for (const day of (currentRoute?.daysPlan || [])) {
    for (const spot of day.spots) {
      if (spot.isRealPlace) {
        allRealSpots.push({
          id: spot.id,
          name: spot.name,
          day: day.day,
          status: spotStatus[spot.id] || spot.status,
          placeId: spot.placeId,
        });
      }
    }
  }

  const completedCount = getCompletedCount();
  const totalSpots = allRealSpots.length;

  // 找当前进行中的天和下一站
  const nextSpot = getNextSpot();
  let currentDay = 1;
  let todayRemaining = 0;

  if (nextSpot && currentRoute) {
    for (const day of currentRoute.daysPlan) {
      const found = day.spots.find((s) => s.id === nextSpot.id);
      if (found) {
        currentDay = day.day;
        const dayRealSpots = day.spots.filter((s) => s.isRealPlace);
        todayRemaining = dayRealSpots.filter(
          (s) => (spotStatus[s.id] || s.status) !== 'completed',
        ).length;
        break;
      }
    }
  } else if (completedCount >= totalSpots && totalSpots > 0) {
    currentDay = currentRoute?.days || 1;
  }

  const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
    completed: { label: '已游玩', color: 'text-bamboo', icon: 'ri-check-line' },
    'in-progress': { label: '进行中', color: 'text-tea', icon: 'ri-play-circle-line' },
    'not-started': { label: '未开始', color: 'text-text-muted', icon: 'ri-time-line' },
    skipped: { label: '已跳过', color: 'text-text-muted', icon: 'ri-close-line' },
  };

  // 判断是否可跳转到地点详情（placeId 存在且在 placesData 中）
  const canNavigateToPlace = (placeId?: string) => {
    if (!placeId) return false;
    return places.some((p) => p.id === placeId);
  };

  // 空状态：没有任何行程数据
  if (!hasCurrentRoute && selectedSpots.length === 0) {
    return (
      <div className="min-h-screen bg-bg pb-20">
        <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
          <h1 className="text-xl font-bold text-text-primary">我的行程</h1>
          <p className="text-sm text-text-muted mt-1">查看和管理你的安吉行程</p>
        </header>
        <div className="flex flex-col items-center justify-center py-20 text-text-muted">
          <div className="w-20 h-20 rounded-full bg-bamboo-pale flex items-center justify-center mb-4">
            <i className="ri-suitcase-line text-bamboo text-3xl" />
          </div>
          <p className="text-base font-medium text-text-primary">还没有行程</p>
          <p className="text-sm text-text-muted mt-1">从找地方或生成路线开始</p>
          <div className="flex gap-3 mt-6">
            <button
              className="px-5 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white"
              onClick={() => navigate('/explore')}
            >
              去找地方
            </button>
            <button
              className="px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
              onClick={() => navigate('/create-route')}
            >
              生成路线
            </button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // 有已选地点但还没生成路线
  if (!hasCurrentRoute && selectedSpots.length > 0) {
    return (
      <div className="min-h-screen bg-bg pb-20">
        <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
          <h1 className="text-xl font-bold text-text-primary">我的行程</h1>
          <p className="text-sm text-text-muted mt-1">查看和管理你的安吉行程</p>
        </header>

        <div className="px-4 mt-6">
          <div className="bg-white rounded-xl p-5 text-center">
            <div className="w-14 h-14 rounded-full bg-bamboo-pale flex items-center justify-center mx-auto mb-3">
              <i className="ri-map-pin-add-line text-bamboo text-2xl" />
            </div>
            <p className="text-base font-medium text-text-primary">已选 {selectedSpots.length} 个地点</p>
            <p className="text-sm text-text-muted mt-1">还未生成完整路线，可以帮你自动安排顺路行程</p>
            <button
              className="mt-4 px-5 py-2.5 rounded-lg bg-bamboo text-white text-sm font-medium cursor-pointer"
              onClick={() => navigate('/create-route')}
            >
              生成路线
            </button>
            <button
              className="mt-2 block mx-auto text-sm text-bamboo cursor-pointer"
              onClick={() => navigate('/explore')}
            >
              继续添加地点
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {selectedSpots.map((id) => {
              const place = places.find((p) => p.id === id);
              return (
                <div key={id} className="bg-white rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-bamboo-pale flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-bamboo text-xs" />
                    </div>
                    <span className="text-sm text-text-body">{place?.name || id}</span>
                  </div>
                  <span className="text-xs text-bamboo bg-bamboo/10 px-2 py-1 rounded">已加入</span>
                </div>
              );
            })}
          </div>
        </div>

        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-bg px-4 pt-3 pb-2">
        <h1 className="text-xl font-bold text-text-primary">我的行程</h1>
        <p className="text-sm text-text-muted mt-1">查看和管理你的安吉行程</p>
      </header>

      {/* Current Trip Card */}
      <div className="px-4 mt-3">
        <div className="bg-white rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-text-primary">{currentRoute!.name}</h2>
              <p className="text-xs text-text-muted mt-1">
                {nextSpot ? `今日 Day ${currentDay}` : completedCount >= totalSpots ? '行程已完成' : '准备出发'}
                {' · '}已游玩 {completedCount}/{totalSpots}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-bamboo">
                {completedCount}
                <span className="text-sm font-normal text-text-muted">/{totalSpots}</span>
              </p>
              <p className="text-[10px] text-text-muted">已打卡</p>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-divider">
            <div className="flex items-center justify-between">
              <div>
                {nextSpot ? (
                  <>
                    <p className="text-xs text-text-muted">今日还剩 {todayRemaining} 个地点</p>
                    <p className="text-sm font-medium text-text-primary mt-0.5">
                      <i className="ri-map-pin-line text-bamboo-light mr-1" />
                      下一站：{nextSpot.name}
                    </p>
                  </>
                ) : completedCount >= totalSpots && totalSpots > 0 ? (
                  <p className="text-sm font-medium text-bamboo">
                    <i className="ri-check-double-line mr-1" />
                    行程已全部完成，生成旅行记录图
                  </p>
                ) : (
                  <p className="text-xs text-text-muted">行程未开始，点击继续行程开始打卡</p>
                )}
              </div>
              <button
                className="px-4 py-2 rounded-lg bg-bamboo text-white text-sm font-medium cursor-pointer whitespace-nowrap"
                onClick={() => navigate('/route-result')}
              >
                {completedCount >= totalSpots && totalSpots > 0 ? '查看行程' : '继续行程'}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="h-1.5 bg-bamboo-pale rounded-full overflow-hidden">
              <div
                className="h-full bg-bamboo rounded-full transition-all"
                style={{ width: `${totalSpots > 0 ? (completedCount / totalSpots) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Warnings */}
      <div className="px-4 mt-3 space-y-2">
        {totalSpots > 8 && (
          <div className="bg-tea/10 rounded-xl p-3 flex items-start gap-2">
            <i className="ri-alert-line text-tea text-sm mt-0.5" />
            <p className="text-xs text-text-body">地点较多，建议分成两天游玩更从容</p>
          </div>
        )}
        {totalSpots > 0 && (
          <div className="bg-stream/10 rounded-xl p-3 flex items-start gap-2">
            <i className="ri-information-line text-stream text-sm mt-0.5" />
            <p className="text-xs text-text-body">生成路线时会优先建议删减距离较远的地点</p>
          </div>
        )}
      </div>

      {/* Spots List —— 只展示真实点位 */}
      <div className="px-4 mt-4">
        <h3 className="text-sm font-bold text-text-primary mb-2">行程地点</h3>
        <div className="space-y-2">
          {allRealSpots.map((spot, i) => {
            const cfg = statusConfig[spot.status] || statusConfig['not-started'];
            const clickable = canNavigateToPlace(spot.placeId);
            return (
              <div
                key={spot.id}
                className={`bg-white rounded-xl p-3 flex items-center gap-3 ${
                  clickable ? 'cursor-pointer' : ''
                }`}
                onClick={() => {
                  if (clickable && spot.placeId) {
                    navigate(`/place/${spot.placeId}`);
                  }
                }}
              >
                <div className="w-8 h-8 rounded-full bg-bamboo-pale flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-bamboo">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">{spot.name}</p>
                  <p className="text-[10px] text-text-muted">Day {spot.day}</p>
                </div>
                <span className={`text-xs flex items-center gap-1 ${cfg.color}`}>
                  <i className={cfg.icon} />
                  {cfg.label}
                </span>
                {clickable && (
                  <i className="ri-arrow-right-s-line text-text-muted text-sm" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="fixed bottom-16 left-0 right-0 z-40 px-4">
        <div className="flex gap-2 max-w-lg mx-auto">
          <button
            className="flex-1 py-2.5 rounded-lg border border-divider text-sm font-medium text-text-body cursor-pointer bg-white"
            onClick={() => navigate('/create-route')}
          >
            生成路线
          </button>
          <button
            className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white bg-bamboo cursor-pointer"
            onClick={() => navigate('/share')}
          >
            生成旅行记录图
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}